import { Injectable } from "@angular/core";
import { Observable, from as observableFrom } from "rxjs";
import { map, mergeMap, switchMap } from "rxjs/operators";

import {
  ResponseEnvelope,
  RestHttp,
  makeResponseEnvelope
} from "@lantern/app/core/api";
import { RuntimeConfigService } from "@lantern/app/core/utils/runtime-config";

import {
  Mart,
  MartsResponse,
  UpdateUserInfoCmd,
  User
} from "@local/app/models";

@Injectable()
export class GlobalAuthApi {
  private basePath = this.runtimeConfig.value.then(({ globalAuthUrl }) => {
    return `${globalAuthUrl}/api/global-auth`;
  });

  constructor(
    private runtimeConfig: RuntimeConfigService,
    private rest: RestHttp
  ) {}

  getMarts(jwt: string): Observable<ResponseEnvelope<Mart>> {
    return observableFrom(this.basePath).pipe(
      switchMap((basePath) => {
        return this.rest
          .get<MartsResponse>(`${basePath}/marts`, undefined, "json", {
            authorization: `Bearer ${jwt}`
          })
          .pipe(
            map(({ data, totalCount }) => {
              return makeResponseEnvelope(data, totalCount);
            })
          );
      })
    );
  }

  // returns a jwt
  loginWithGoogle(credential: string): Observable<{ jwt: string }> {
    interface Request {
      credential: string;
    }

    interface Response {
      data: {
        jwt: string;
      };
    }

    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest.post<Request, Response>(
          `${basePath}/auth/actions/login/google-id-token`,
          {
            credential
          }
        );
      }),
      map((envelope) => envelope.data)
    );
  }

  // sends a magic link to the user's email
  loginWithPassword(
    email: string,
    password: string,
    recaptchaResponse: string
  ): Observable<{ success: true }> {
    interface Request {
      email: string;
      password: string;
      recaptchaResponse: string;
    }

    interface Response {
      success: true;
    }

    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest.post<Request, Response>(
          `${basePath}/auth/actions/login/password`,
          {
            email,
            password,
            recaptchaResponse
          }
        );
      })
    );
  }

  loginWithMagicToken(magicToken: string): Observable<{ jwt: string }> {
    interface Response {
      data: {
        jwt: string;
      };
    }

    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest.get<Response>(
          `${basePath}/auth/actions/login/magic-token?token=${magicToken}`
        );
      }),
      map((envelope) => envelope.data)
    );
  }

  // clear all user ui jwts
  logout(jwt: string): Observable<{ success: boolean }> {
    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest.post<null, { success: boolean }>(
          `${basePath}/auth/actions/logout`,
          null,
          undefined,
          "json",
          {
            authorization: `Bearer ${jwt}`
          }
        );
      })
    );
  }

  // check jwt is still valid (use to re-login if already logged in)
  verifyToken(jwt: string): Observable<{ valid: boolean; expiresAt?: Date }> {
    interface Response {
      data: {
        valid: boolean;
        expiresAt?: string;
      };
    }

    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest.post<null, Response>(
          `${basePath}/auth/actions/verify-token`,
          null,
          undefined,
          "json",
          {
            authorization: `Bearer ${jwt}`
          }
        );
      }),
      map((envelope) => {
        const { valid, expiresAt } = envelope.data;
        return {
          valid,
          expiresAt: expiresAt != null ? new Date(expiresAt) : undefined
        };
      })
    );
  }

  // sends an email for password reset (always returns success!)
  requestResetPassword(
    email: string,
    returnUrl: string
  ): Observable<{ success: boolean }> {
    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest.post<any, { success: boolean }>(
          `${basePath}/auth/actions/request-reset-password`,
          {
            email,
            returnUrl
          }
        );
      })
    );
  }

  // should be able to use email and password to login thereafter
  resetPassword(
    email: string,
    password: string,
    token: string
  ): Observable<{ success: boolean }> {
    interface Request {
      email: string;
      password: string;
      token: string;
    }

    interface Response {
      success: boolean;
    }

    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest.post<Request, Response>(
          `${basePath}/auth/actions/reset-password`,
          {
            email,
            password,
            token
          }
        );
      })
    );
  }

  getUserInfo(jwt: string): Observable<User> {
    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest
          .get<{ data: User }>(
            `${basePath}/auth/user-info`,
            undefined,
            "json",
            {
              authorization: `Bearer ${jwt}`
            }
          )
          .pipe(map(({ data: user }) => user));
      })
    );
  }

  updateUserInfo(
    cmd: UpdateUserInfoCmd,
    email: string,
    jwt: string
  ): Observable<{ success: boolean }> {
    interface Request {
      name?: string;
      password?: string;
      existingPassword?: string;
    }

    interface Response {
      success: boolean;
    }

    const request = {} as Request;
    if (cmd.name != null) {
      request.name = cmd.name;
    }
    if (cmd.password != null) {
      request.password = cmd.password;
      request.existingPassword = cmd.existingPassword;
    }

    return observableFrom(this.basePath).pipe(
      mergeMap((basePath) => {
        return this.rest.post<Request, Response>(
          `${basePath}/auth/actions/update-user-info`,
          request,
          undefined,
          "json",
          {
            authorization: `Bearer ${jwt}`
          }
        );
      })
    );
  }
}
